// src/components/NotificationsBell.tsx
import { createPortal } from "react-dom";
import { useEffect, useState, useMemo, useRef } from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  writeBatch,            // + add
  serverTimestamp,       // + add
  updateDoc,             // + add (optional: profile stamp)
  deleteDoc,             // + add (fallback not required, batch.delete is enough)
  type Timestamp,
  type Unsubscribe,
} from "firebase/firestore";
import { auth, db } from "@/lib/firebase";
import { Link, useNavigate } from "react-router-dom";

/* =========================
   Types (narrow, resilient)
========================= */

type ConversationDoc = {
  participants?: string[];                 // [uidA, uidB]
  updatedAt?: Timestamp;
  lastMessage?: string | null;
  lastMessageAt?: Timestamp;
  lastSenderId?: string | null;
  unreadCounts?: Record<string, number>;   // { [uid]: number }
};

type FollowStatus = "approved" | "pending";
type FollowDoc = {
  followerId?: string;
  followingId?: string;
  status?: FollowStatus;
  createdAt?: Timestamp;
};

type UserDoc = {
  username?: string | null;
  username_lower?: string | null;
  display_name?: string | null;
  displayName?: string | null; // legacy
  avatar_url?: string | null;
  photoURL?: string | null;
};

type MinimalUser = {
  uid: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
};

/* =========================
   Small utils
========================= */

function pickUsernameLike(u: UserDoc): string {
  const s =
    (u.username ??
      u.username_lower ??
      u.display_name ??
      u.displayName ??
      "")?.toString()?.trim() || "";
  return s || "player";
}

function pickAvatar(u: UserDoc): string | null {
  const a = (u.avatar_url ?? u.photoURL ?? "")?.toString()?.trim() || "";
  return a || null;
}

function timeShort(ts?: Timestamp): string {
  if (!ts) return "";
  const d = ts.toDate();
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((s) => s[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

// Match Tailwind sm breakpoint (< 640px)
function useSmallScreen(): boolean {
  const [small, setSmall] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 639px)").matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const onChange = () => setSmall(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return small;
}

/* =========================
   Component
========================= */

export default function NotificationsBell(): JSX.Element {
  const [uid, setUid] = useState<string | null>(auth.currentUser?.uid ?? null);
  const [ready, setReady] = useState<boolean>(!!auth.currentUser);
  const isSmall = useSmallScreen();
  const [loadingConvos, setLoadingConvos] = useState(true);
  const [loadingFollows, setLoadingFollows] = useState(true);
  const [loadingOffers, setLoadingOffers] = useState(true);
  const [open, setOpen] = useState(false);
  const [clearing, setClearing] = useState(false); // + add
  const [useCounters, setUseCounters] = useState(false); // prefer server counters when available
  const [badgeMuteUntil, setBadgeMuteUntil] = useState<number>(0); // mute window to keep badge at 0 right after open/clear

  // Keep uid in sync with auth
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (u) => {
      setUid(u?.uid ?? null);
      setReady(true);
    });
    return () => unsubAuth();
  }, []);

  // Conversations preview + unread
  const [msgTotal, setMsgTotal] = useState(0);
  const [topConvos, setTopConvos] = useState<
    Array<{
      id: string;
      other: MinimalUser | null;
      unread: number;
      lastMessage: string;
      lastMessageAt?: Timestamp;
    }>
  >([]);

  // Pending follow requests
  const [pendingFollows, setPendingFollows] = useState(0);

  // Offers (optional / schema-agnostic)
  const [offersTotal, setOffersTotal] = useState(0);

  const nav = useNavigate();
  const ref = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Open a conversation in Inbox (messages tab), by thread or peer uid
  function openConversation(id: string, otherUid?: string | null) {
    const qp = id
      ? `tab=messages&thread=${encodeURIComponent(id)}`
      : otherUid
      ? `tab=messages&to=${encodeURIComponent(otherUid)}`
      : "tab=messages";
    nav(`/inbox?${qp}`);
    setOpen(false);
  }

  // Close on outside click (supports portal-rendered panel)
  useEffect(() => {
    function onDocPointerDown(e: MouseEvent | TouchEvent) {
      const t = e.target as Node | null;
      const insideTrigger = !!(ref.current && t && ref.current.contains(t));
      const insidePanel = !!(panelRef.current && t && panelRef.current.contains(t));
      if (!insideTrigger && !insidePanel) setOpen(false);
    }
    document.addEventListener("mousedown", onDocPointerDown);
    document.addEventListener("touchstart", onDocPointerDown);
    return () => {
      document.removeEventListener("mousedown", onDocPointerDown);
      document.removeEventListener("touchstart", onDocPointerDown);
    };
  }, []);

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Compute top offset below the header for the mobile sheet
  const [sheetTop, setSheetTop] = useState<number>(56);
  useEffect(() => {
    function computeTop() {
      const hdr = document.getElementById("app-topbar");
      if (!hdr) return setSheetTop(56);
      const r = hdr.getBoundingClientRect();
      // Add small gap
      setSheetTop(Math.max(48, Math.round(r.bottom + 8)));
    }
    computeTop();
    window.addEventListener("resize", computeTop);
    return () => window.removeEventListener("resize", computeTop);
  }, []);

  // Live subscriptions (make counts come from collections unless counters doc exists)
  useEffect(() => {
    let unsubFollows: Unsubscribe | null = null;
    let unsubFollowsFallback: Unsubscribe | null = null;
    let unsubOffers: Unsubscribe | null = null;
    let unsubConvos: Unsubscribe | null = null;

    if (!uid) {
      setLoadingFollows(false);
      setLoadingOffers(false);
      setLoadingConvos(false);
      setPendingFollows(0);
      setOffersTotal(0);
      setMsgTotal(0);
      setTopConvos([]);
      return;
    }

    // Threads (was conversations) preview + unread
    setLoadingConvos(true);
    try {
      const convQ = query(
        collection(db, "threads_v2"),
        where("visibleFor", "array-contains", uid),
        orderBy("lastMessageAt", "desc"),
        limit(10)
      );
      unsubConvos = onSnapshot(
        convQ,
        async (snap) => {
          const rows = snap.docs.map((d) => ({ id: d.id, data: d.data() as any }));
          const others = new Set<string>();
          rows.forEach((r) =>
            (r.data.participants || []).forEach((p: string) => {
              if (p !== uid) others.add(p);
            })
          );
          const people = new Map<string, MinimalUser>();
          await Promise.all(
            Array.from(others).map(async (pid) => {
              try {
                const us = await getDoc(doc(db, "users", pid));
                if (us.exists()) {
                  const u = us.data() as UserDoc;
                  people.set(pid, {
                    uid: pid,
                    username: pickUsernameLike(u),
                    displayName: pickUsernameLike(u),
                    avatarUrl: pickAvatar(u),
                  });
                }
              } catch {}
            })
          );
          const convs = rows.map((r) => {
            const otherId =
              (r.data.participants || []).find((p: string) => p !== uid) || "";
            const other = people.get(otherId) || null;
            const unread = (r.data.unreadCounts && r.data.unreadCounts[uid]) || 0;
            return {
              id: r.id,
              other,
              unread,
              lastMessage: r.data.lastMessage || "",
              lastMessageAt: r.data.lastMessageAt,
            };
          });
          setTopConvos(convs);
            if (!useCounters) {
              const totalUnread = convs.reduce(
                (sum, c) => sum + (c.unread || 0),
                0
              );
              setMsgTotal(totalUnread);
            }
          setLoadingConvos(false);
        },
        () => {
          setTopConvos([]);
          if (!useCounters) setMsgTotal(0);
          setLoadingConvos(false);
        }
      );
    } catch {
      setLoadingConvos(false);
    }

    // Pending follow requests: only listen if no counters doc
    if (!useCounters) {
      setLoadingFollows(true);
      const folRef = collection(db, "follows");
      const folPrimary = query(
        folRef,
        where("followingId", "==", uid),
        where("status", "==", "pending")
      );
      const stopPrimaryFollows = onSnapshot(
        folPrimary,
        (snap) => {
          setPendingFollows(snap.size);
          setLoadingFollows(false);
        },
        () => {
          // Fallback 1: count locally without status filter
          const folFallback = query(folRef, where("followingId", "==", uid));
          unsubFollowsFallback = onSnapshot(
            folFallback,
            (snap) => {
              let n = 0;
              snap.forEach((d) => {
                const v = d.data() as { status?: "approved" | "pending" | string | null };
                if ((v?.status ?? "approved") === "pending") n++;
              });
              setPendingFollows(n);
              setLoadingFollows(false);
            },
            () => setLoadingFollows(false)
          );
        }
      );
      unsubFollows = () => stopPrimaryFollows();
    } else {
      setLoadingFollows(false);
    }

    // Offers: only listen if no counters doc
    if (!useCounters) {
      setLoadingOffers(true);
      const v2Ref = collection(db, "offers_v2");
      const v2Q = query(v2Ref, where("toUid", "==", uid), where("status", "==", "open"), limit(50));
      const stopOffers = onSnapshot(
        v2Q,
        (snap) => {
          setOffersTotal(snap.size);
          setLoadingOffers(false);
        },
        () => setLoadingOffers(false)
      );
      unsubOffers = () => stopOffers();
    } else {
      setLoadingOffers(false);
    }

    return () => {
      unsubFollows?.();
      unsubFollowsFallback?.();
      unsubOffers?.();
      unsubConvos?.();
    };
  }, [uid, useCounters]);

  // Prefer counters document if present; ignore it briefly after open/clear (mute)
  useEffect(() => {
    if (!uid) return;
    const stop = onSnapshot(
      doc(db, "users", uid, "counters", "notifications"),
      (snap) => {
        if (!snap.exists()) return;
        // During mute window, don't overwrite local zeroed counts for the badge.
        const muted = Date.now() < badgeMuteUntil;
        const v = snap.data() as any;
        setUseCounters(true);
        if (!muted) {
          setMsgTotal(Number(v?.messagesUnread || 0));
          setPendingFollows(Number(v?.followsPending || 0));
          setOffersTotal(Number(v?.offersOpen || 0));
        }
      },
      () => setUseCounters(false)
    );
    return () => stop();
  }, [uid, badgeMuteUntil]);

  // Unified badge count (sum of categories); muted to 0 after open/clear
  const total = (msgTotal || 0) + (pendingFollows || 0) + (offersTotal || 0);
  const badgeCount = Date.now() < badgeMuteUntil ? 0 : total;
  const badgeText = useMemo(() => {
    if (badgeCount <= 0) return "";
    if (badgeCount > 99) return "99+";
    if (badgeCount > 9) return "9+";
    return String(badgeCount);
  }, [badgeCount]);

  // Mark notifications collection docs as read (on open), but keep previews
  async function markAllSeenAndResetBadge(): Promise<void> {
    if (!uid) return;
    try {
      const unreadQ = query(
        collection(db, "users", uid, "notifications"),
        where("isRead", "==", false),
        limit(200)
      );
      const snap = await getDocs(unreadQ);
      if (!snap.empty) {
        const batch = writeBatch(db);
        snap.forEach((d) => {
          batch.update(d.ref, { isRead: true, readAt: serverTimestamp() });
        });
        await batch.commit();
      }
      try {
        await updateDoc(doc(db, "users", uid), {
          notif_last_opened: serverTimestamp(),
        });
      } catch {}
      // Optimistic: zero message badge locally (server counters catch up)
      setTopConvos((prev) => prev.map((c) => ({ ...c, unread: 0 })));
      if (!useCounters) setMsgTotal(0);
    } catch (e) {
      console.error("[NotificationsBell] markAllSeen error:", e);
    } finally {
      // Mute badge so it doesn't flash back while counters/streams catch up
      setBadgeMuteUntil(Date.now() + 15000);
    }
  }

  // Mark all message threads as read for this user (unreadCounts.<uid> = 0)
  async function markAllConversationsReadForUser(): Promise<void> {
    if (!uid) return;
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const convQ = query(
          collection(db, "threads_v2"),
          where("visibleFor", "array-contains", uid),
          limit(400)
        );
        const snap = await getDocs(convQ);
        if (snap.empty) break;

        const batch = writeBatch(db);
        snap.forEach((d) => {
          batch.update(d.ref, { [`unreadCounts.${uid}`]: 0 });
        });
        await batch.commit();

        await new Promise((r) => setTimeout(r, 0));
        if (snap.size < 400) break;
      }
      // Optimistic local reset
      setTopConvos((prev) => prev.map((c) => ({ ...c, unread: 0 })));
      setMsgTotal(0);
    } catch (e) {
      console.error("[NotificationsBell] markAllConversationsReadForUser failed:", e);
    }
  }

  // Clear all notifications docs, zero unread counts, remove previews; keep badge muted briefly
  async function clearAllNotifications(): Promise<void> {
    if (!uid) return;
    setClearing(true);
    try {
      // Delete notification docs in batches
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const allQ = query(collection(db, "users", uid, "notifications"), limit(500));
        const snap = await getDocs(allQ);
        if (snap.empty) break;
        const batch = writeBatch(db);
        snap.forEach((d) => batch.delete(d.ref));
        await batch.commit();
        await new Promise((r) => setTimeout(r, 0));
        if (snap.size < 500) break;
      }

      // Zero conversations unread and remove previews
      await markAllConversationsReadForUser();
      setTopConvos([]); // remove previews immediately
      setPendingFollows(0);
      setOffersTotal(0);
    } catch (e) {
      console.warn("[NotificationsBell] delete failed; marking cleared instead.", e);
      try {
        const allQ = query(collection(db, "users", uid, "notifications"), limit(500));
        const snap = await getDocs(allQ);
        if (!snap.empty) {
          const batch = writeBatch(db);
          snap.forEach((d) => batch.update(d.ref, { isRead: true, cleared: true, clearedAt: serverTimestamp() }));
          await batch.commit();
        }
        await markAllConversationsReadForUser();
        setTopConvos([]);
        setPendingFollows(0);
        setOffersTotal(0);
      } catch (e2) {
        console.error("[NotificationsBell] clear fallback failed:", e2);
      }
    } finally {
      setClearing(false);
      setBadgeMuteUntil(Date.now() + 7000);
    }
  }

  // REMOVE duplicate counters subscription block at the bottom of the file
  // useEffect(() => { ... subscribe to users/{uid}/counters/notifications ... }, [uid]);

  // Render
  if (!uid) {
    // Signed-out users: simple bell → splash/sign-in
    return (
      <Link
        to="/splash"
        className="relative grid h-9 w-9 place-items-center rounded-full border border-gray-700 text-white/90 hover:bg-white/5"
        aria-label="Sign in to view notifications"
        title="Sign in to view notifications"
      >
        <NotifyIcon />
      </Link>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Notifications"
        title="Notifications"
        onClick={async () => {
          const next = !open;
          setOpen(next);
          if (next) await markAllSeenAndResetBadge(); // clear badge, keep previews
        }}
        className={[
          "relative grid h-9 w-9 place-items-center rounded-full border border-gray-700 text-white transition",
          open ? "ring-2 ring-white/20" : "hover:bg-white/5",
        ].join(" ")}
        data-badge={badgeText || undefined}
      >
        <NotifyIcon />
        {badgeText && (
          <span
            aria-hidden
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-red-600 text-white text-[11px] font-bold"
          >
            {badgeText}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Mobile sheet */}
          {isSmall &&
            createPortal(
              <div
                role="menu"
                ref={panelRef}
                className="fixed z-[100] rounded-xl border border-gray-700 bg-gray-900/95 backdrop-blur shadow-2xl overflow-hidden flex flex-col sm:hidden"
                style={{ left: 8, right: 8, top: sheetTop, bottom: 8, WebkitTransform: "translateZ(0)", contain: "layout" }}
              >
                <Panel
                  msgTotal={msgTotal}
                  loadingConvos={loadingConvos}
                  pendingFollows={pendingFollows}
                  loadingFollows={loadingFollows}
                  offersTotal={offersTotal}
                  loadingOffers={loadingOffers}
                  topConvos={topConvos}
                  onClose={() => setOpen(false)}
                  onClear={() => void clearAllNotifications()}
                  clearing={clearing}
                  nav={nav}
                  onOpenConversation={(id, otherUid) => void openConversation(id, otherUid)}
                />
              </div>,
              document.body
            )}

          {/* Desktop dropdown */}
          {!isSmall && (
            <div role="menu" className="absolute right-0 mt-2 w-[min(92vw,380px)] z-50 hidden sm:block">
              <div ref={panelRef} className="rounded-lg border border-gray-700 bg-gray-900 shadow-xl overflow-hidden flex max-h-[80vh] flex-col">
                <Panel
                  msgTotal={msgTotal}
                  loadingConvos={loadingConvos}
                  pendingFollows={pendingFollows}
                  loadingFollows={loadingFollows}
                  offersTotal={offersTotal}
                  loadingOffers={loadingOffers}
                  topConvos={topConvos}
                  onClose={() => setOpen(false)}
                  onClear={() => void clearAllNotifications()}
                  clearing={clearing}
                  nav={nav}
                  onOpenConversation={(id, otherUid) => void openConversation(id, otherUid)}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* =========================
   Panel content (shared)
========================= */

function Panel({
  msgTotal,
  pendingFollows,
  offersTotal,
  loadingConvos,
  loadingFollows,
  loadingOffers,
  topConvos,
  onClose,
  onClear,
  clearing,
  nav,
  onOpenConversation,
}: {
  msgTotal: number;
  pendingFollows: number;
  offersTotal: number;
  loadingConvos: boolean;
  loadingFollows: boolean;
  loadingOffers: boolean;
  topConvos: Array<{
    id: string;
    other: MinimalUser | null;
    unread: number;
    lastMessage: string;
    lastMessageAt?: Timestamp;
  }>;
  onClose: () => void;
  onClear: () => void;
  clearing: boolean;
  nav: ReturnType<typeof useNavigate>;
  onOpenConversation: (id: string, otherUid?: string | null) => void;
}): JSX.Element {
  const total = (msgTotal || 0) + (pendingFollows || 0) + (offersTotal || 0);
  const unreadConvos = topConvos.filter((c) => (c.unread || 0) > 0);

  return (
    <>
      {/* Header (sticky) */}
      <div className="px-3 py-2 border-b border-gray-800 text-white font-semibold shrink-0 flex items-center justify-between">
        <span>Notifications</span>
        <button
          type="button"
          onClick={onClear}
          disabled={clearing}
          className="text-xs px-2 py-1 rounded border border-gray-700 text-white/90 hover:bg-white/5 disabled:opacity-50"
          title="Clear all notifications"
        >
          {clearing ? "Clearing…" : "Clear"}
        </button>
      </div>

      {/* Scrollable content */}
      <div className="min-h-0 overflow-y-auto overscroll-contain">
        {/* Summary tiles */}
        <div className="grid grid-cols-3 gap-2 p-3">
          <SummaryTile
            label="Messages"
            count={msgTotal}
            loading={loadingConvos}
            onClick={() => {
              onClose();
              nav("/inbox?tab=messages");
            }}
          />
          <SummaryTile
            label="Follow requests"
            count={pendingFollows}
            loading={loadingFollows}
            onClick={() => {
              onClose();
              nav("/followers?tab=requests");
            }}
          />
          <SummaryTile
            label="Offers"
            count={offersTotal}
            loading={loadingOffers}
            onClick={() => {
              onClose();
              nav("/inbox?tab=offers");
            }}
          />
        </div>

        {/* Details: Unread message previews only */}
        <div className="px-3 pb-3">
          <div className="text-xs text-white/70 mb-1">Recent</div>
          {loadingConvos ? (
            <div className="text-white/80 text-sm p-2">Loading…</div>
          ) : unreadConvos.length === 0 && pendingFollows === 0 && offersTotal === 0 ? (
            <div className="text-white/80 text-sm p-2">You’re all caught up.</div>
          ) : (
            <ul className="divide-y divide-gray-800 rounded border border-gray-800 overflow-hidden">
              {unreadConvos.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-center gap-3"
                    onClick={() => {
                      const otherUid = c.other?.uid ?? null;
                      onClose();
                      onOpenConversation(c.id, otherUid);
                    }}
                  >
                    <Avatar
                      img={c.other?.avatarUrl}
                      name={c.other?.username ?? "player"}
                      size={28}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <div className="truncate text-sm text-white">
                          @{c.other?.username ?? "player"}
                        </div>
                        <div className="text[11px] text-white/60 ml-2 shrink-0">
                          {timeShort(c.lastMessageAt)}
                        </div>
                      </div>
                      <div className="text-xs text-white/70 truncate">
                        {c.unread > 0 ? `(${c.unread} new) ` : ""}
                        {c.lastMessage || "New messages"}
                      </div>
                    </div>
                  </button>
                </li>
              ))}

              {/* Follow requests hint */}
              {pendingFollows > 0 && (
                <li>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-center gap-3"
                    onClick={() => {
                      onClose();
                      nav("/followers?tab=requests");
                    }}
                  >
                    <div className="grid place-items-center rounded-full bg-indigo-500/20 border border-indigo-400/40 text-indigo-200 w-7 h-7 text-xs font-bold">
                      {pendingFollows > 99 ? "99+" : pendingFollows}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm text-white">Pending follow requests</div>
                      <div className="text-xs text-white/70 truncate">Review and approve</div>
                    </div>
                  </button>
                </li>
              )}

              {/* Offers hint */}
              {offersTotal > 0 && (
                <li>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-center gap-3"
                    onClick={() => {
                      onClose();
                      nav("/inbox?tab=offers");
                    }}
                  >
                    <div className="grid place-items-center rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-200 w-7 h-7 text-xs font-bold">
                      {offersTotal > 99 ? "99+" : offersTotal}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm text-white">Open offers</div>
                      <div className="text-xs text-white/70 truncate">View in InboxV2</div>
                    </div>
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>
      </div>

      {/* Footer actions (sticky) */}
      <div className="flex items-center justify-between px-3 py-2 border-t border-gray-800 text-sm shrink-0">
        <Link to="/inbox?tab=messages" data-tour="messages" onClick={onClose} className="text-white/90 hover:underline">
          Open Inbox
        </Link>
        <Link to="/followers" onClick={onClose} className="text-white/90 hover:underline">
          Manage Followers
        </Link>
      </div>
    </>
  );
}

/* =========================
   Small pieces
========================= */

function SummaryTile({
  label,
  count,
  loading,
  onClick,
}: {
  label: string;
  count: number;
  loading?: boolean;
  onClick: () => void;
}): JSX.Element {
  const has = count > 0;
  const text =
    count > 99 ? "99+" : count > 9 ? "9+" : count > 0 ? String(count) : "0";
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-md border px-3 py-2 text-left transition",
        has
          ? "border-indigo-500/50 bg-indigo-500/10 text-white"
          : "border-white/10 bg-white/5 text-white/90 hover:bg-white/10",
      ].join(" ")}
      aria-label={`${label}: ${count}`}
      title={`${label}: ${count}`}
    >
      <div className="text-xs opacity-80">{label}</div>
      <div className="text-lg font-semibold">
        {loading ? <span className="opacity-70">…</span> : text}
      </div>
    </button>
  );
}

function Avatar({
  img,
  name,
  size = 28,
}: {
  img: string | null | undefined;
  name: string;
  size?: number;
}): JSX.Element {
  const init = initialsOf(name);
  return (
    <div
      className="overflow-hidden rounded-full border border-gray-700 bg-white/5 text-white/80 grid place-items-center"
      style={{ width: size, height: size }}
    >
      {img ? (
        <img
          src={img}
          className="h-full w-full object-cover"
          alt="avatar"
          referrerPolicy="no-referrer"
          decoding="async"
        />
      ) : (
        <span className="text-[10px] font-bold">{init}</span>
      )}
    </div>
  );
}

// Replace BellIcon with a PNG-based icon
function NotifyIcon(): JSX.Element {
  return (
    <img
      src="/images/notify.png"
      alt=""
      aria-hidden="true"
      className="h-5 w-5 object-contain"
      decoding="async"
    />
  );
}

// Example (do not render here):
// Link targets:
// - Messages: /inbox?tab=messages
// - Offers:   /inbox?tab=offers
// - Followers:/followers
